/**
 * Cesium 1.137.0 bug fix — _createTileImagerySkeletons crash guard.
 *
 * BUG: In _createTileImagerySkeletons, the initial clippedImageryRectangle
 * (intersection of the NW imagery tile with imagery bounds) can be undefined
 * when the rectangle constraint causes boundary misalignment. Cesium passes
 * this undefined to rectangleToNativeRectangle() and uses it unguarded for
 * initial maxU/minV calculations, causing:
 *   TypeError: Cannot read properties of undefined (reading 'west')
 *
 * FIX STRATEGY: We monkey-patch _createTileImagerySkeletons on ImageryLayer.
 * Since we can't easily modify Cesium's internal minified code, we wrap the
 * entire method in a try/catch for the specific TypeError. When the crash
 * would occur at a boundary tile, we fall back to calling the method WITHOUT
 * the layer's rectangle constraint (using MAX_VALUE), which produces the
 * correct tile skeletons. The layer rectangle only affects skeleton generation,
 * not actual tile rendering bounds — the imagery provider's rectangle already
 * constrains what tiles are fetched/drawn.
 *
 * Must be called ONCE before any Cesium Viewer is created.
 */

import * as Cesium from 'cesium';

let patched = false;

export function patchCesiumSkeletons(): void {
  if (patched) return;
  patched = true;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ILProto = (Cesium.ImageryLayer as any).prototype;
  const originalCreateSkeletons = ILProto._createTileImagerySkeletons;

  if (typeof originalCreateSkeletons !== 'function') {
    console.warn('[CesiumPatch] _createTileImagerySkeletons not found — skipping patch');
    return;
  }

  ILProto._createTileImagerySkeletons = function (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tile: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    terrainProvider: any,
    insertionPoint?: number,
  ): boolean {
    try {
      return originalCreateSkeletons.call(this, tile, terrainProvider, insertionPoint);
    } catch (e) {
      if (
        e instanceof TypeError &&
        e.message?.includes('Cannot read properties of undefined')
      ) {
        // The crash occurs because clippedImageryRectangle (intersection of the
        // NW imagery tile with imageryBounds) is undefined — the NW tile is
        // outside the imagery bounds.
        //
        // Fallback: temporarily widen this._rectangle to MAX_VALUE so the
        // intersection cannot fail, then call the original method. This is safe
        // because the provider's own rectangle still constrains which tiles are
        // actually fetched. The layer rectangle is only used for the
        // imageryBounds intersection computation.
        const savedRect = this._rectangle;
        this._rectangle = Cesium.Rectangle.MAX_VALUE;
        try {
          const result = originalCreateSkeletons.call(this, tile, terrainProvider, insertionPoint);
          this._rectangle = savedRect;
          return result;
        } catch {
          this._rectangle = savedRect;
          // If it still fails, just skip this tile — no skeletons for this layer
          return false;
        }
      }
      // Re-throw any non-related error
      throw e;
    }
  };

  console.log('[CesiumPatch] Applied _createTileImagerySkeletons crash guard');
}
